import { useSyncExternalStore } from "react";
import type { DawnMode, DawnTarget } from "./dawnPlan";

/**
 * Live state of the dawn transition, shared between the start page (which runs it)
 * and the overlay (which renders it). A module-level store, no provider needed.
 */

export type DawnPhase = "idle" | "preparing" | "running";

/** The dawn's two looks, or the 200 ms root crossfade for reduced motion and more contrast. */
export type DawnStoreMode = DawnMode | "crossfade";

export interface DawnState {
  phase: DawnPhase;
  target: DawnTarget | null;
  mode: DawnStoreMode | null;
  /** True while the fallback without the View Transitions API runs (the overlay's CSS beats, dawn.css). */
  fallback: boolean;
}

export const DAWN_IDLE: DawnState = Object.freeze({
  phase: "idle",
  target: null,
  mode: null,
  fallback: false,
});

let state: DawnState = DAWN_IDLE;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of [...listeners]) {
    try {
      listener();
    } catch (error) {
      // One failing listener must neither starve the others nor break the caller.
      queueMicrotask(() => {
        throw error;
      });
    }
  }
}

export const dawnStore = {
  get: (): DawnState => state,
  set: (patch: Partial<DawnState>): void => {
    const keys = Object.keys(patch) as Array<keyof DawnState>;
    if (keys.every((key) => patch[key] === state[key])) return;
    state = { ...state, ...patch };
    emit();
  },
  reset: (): void => {
    state = DAWN_IDLE;
    emit();
  },
  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};

export function useDawnState(): DawnState {
  return useSyncExternalStore(dawnStore.subscribe, dawnStore.get, dawnStore.get);
}

/**
 * The app's own `theme-color` (index.html), read once by DawnHost when the app starts, before any
 * page can change it: the value the status bar returns to after a dawn. `undefined` until read,
 * `null` for a document without a value.
 */
let appThemeColor: string | null | undefined;

/** Remembers the document's `theme-color` the first time it is called; later calls keep it. */
export function captureAppThemeColor(doc: Document): void {
  if (appThemeColor !== undefined) return;
  appThemeColor = doc.querySelector('meta[name="theme-color"]')?.getAttribute("content") ?? null;
}

export function getAppThemeColor(): string | null | undefined {
  return appThemeColor;
}

/** Forgets the remembered value (tests). */
export function resetAppThemeColor(): void {
  appThemeColor = undefined;
}
